import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { BuyCreditsDialogComponent } from '../../components/dialogs/buy-credits-dialog/buy-credits-dialog.component';
import { CreditsInfoDialogComponent } from '../../components/dialogs/credits-info-dialog/credits-info-dialog.component';
import {
  AutoRechargeDialogComponent,
  AutoRechargeDialogResult,
} from '../../components/dialogs/auto-recharge-dialog/auto-recharge-dialog.component';
import { CreditPricing, CreditService, CreditSettings } from '../../services/credit.service';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { InviteUserDialogComponent } from '../../components/dialogs/invite-user-dialog/invite-user-dialog.component';
import { FeatureSetDialogComponent } from '../../components/dialogs/feature-set-dialog/feature-set-dialog.component';
import { AvvDialogComponent } from '../../components/dialogs/avv-dialog/avv-dialog.component';
import { UserDialogComponent } from '../../components/dialogs/user-dialog/user-dialog.component';
import { AuthService } from '../../services/auth.service';
import { TwoFactorService } from '../../services/two-factor.service';
import {
  Invoice,
  OrganisationService,
  PaymentMethodInfo,
} from '../../services/organisation.service';
import { ProductService } from '../../services/product.service';
import {
  BYTES_PER_MB,
  CONTRACT_LIMIT_KEYS,
  ClientContract,
  ContractInvoice,
  ContractService,
  lastDayOf,
  openPdfBlob,
} from '../../services/contract.service';
import { User, UserService } from '../../services/user.service';
import {
  FeatureSet,
  FeatureSetService,
} from '../../services/feature-set.service';

@Component({
  selector: 'app-organisation',
  templateUrl: './organisation.component.html',
  styleUrl: './organisation.component.scss',
})
export class OrganisationComponent {
  loading = true;
  /** The organisation is being deleted (many seconds on the server). The page
   *  gives way to the loader so the delete cannot be started a second time. */
  deletingOrganisation = false;
  showFeatureSets = false;

  availablePermissions: string[] = [];

  /** Which section is open. Mirrored into `?tab=` so a reload keeps the
   *  place and support can link straight at a section — the one thing plain
   *  tabs lose compared to routed ones, and it costs two lines to keep. */
  readonly tabIds = ['general', 'billing', 'users', 'security'] as const;
  activeTab: string = 'general';

  /** Tenant-wide second-factor switch (admin card). */
  require2fa = false;
  require2faSaving = false;

  creditsLoading = false;

  /** Payment method Stripe will charge next — admins only (backend enforces it). */
  paymentMethod?: PaymentMethodInfo;
  paymentMethodError = false;
  billingPortalOpening = false;

  /** Id of the invitee whose mail is currently on its way, so the button can
   *  disable itself instead of sending a second password. */
  resendingInvitationFor: string | null = null;

  /** Threshold chips offered in the alert config. */
  alertChoices = [50, 60, 70, 80, 90, 95, 100];

  /** Individueller Vertrag (Migration 081): Konditionen fuer die Karte
   *  „Individueller Vertrag“. Nur fuer admin/super_admin geladen — das
   *  Backend gibt sie anderen Rollen nicht heraus. */
  contract: ClientContract | null = null;
  contractLoading = false;
  /** Kontingent-Zeilen der Vertragskarte, einmal beim Laden gebaut. */
  contractLimitRows: { key: string; value: string }[] = [];
  /** Von alphacount ausgestellte Rechnungen des Vertragskunden (Phase 3). */
  contractInvoices: (ContractInvoice & { periodTo: string | null })[] = [];
  contractInvoicesLoading = false;
  downloadingInvoiceId?: string;

  constructor(
    protected organisationService: OrganisationService,
    protected userService: UserService,
    protected productService: ProductService,
    protected featureSetService: FeatureSetService,
    protected creditService: CreditService,
    protected dialogService: NbDialogService,
    protected authService: AuthService,
    protected toastr: NbToastrService,
    protected translate: TranslateService,
    protected twoFactor: TwoFactorService,
    protected contractService: ContractService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.userService.listUsers().subscribe();
    const requested = this.route.snapshot.queryParamMap.get('tab');
    if (requested && this.tabIds.includes(requested as any)) {
      this.activeTab = requested;
    }
    if (this.authService.isAdmin()) {
      this.twoFactor
        .getRequirement()
        .then((v) => (this.require2fa = v))
        .catch(() => (this.require2fa = false));
    }
    this.productService.getActivePlan().subscribe((plan) => {
      if (plan?.current_plan?.is_contract && this.authService.isAdmin()) {
        this.loadContract();
      }
    });
    // Credits are billing data — only load them for the audience that can see
    // the card (admin, super_admin), so nobody else pays for the round-trips.
    if (this.authService.isAdmin()) {
      this.loadCredits();
    }
    this.organisationService.getOrganisation().subscribe((orga) => {
      this.loading = false;
      if (orga?.client_details?.stripe_customer_id) {
        this.organisationService.getInvoices().subscribe();
        if (this.authService.hasAccess(this.authService.UserAccess.OrganisationManageSubscription)) {
          this.organisationService.getPaymentMethod().subscribe({
            next: (pm) => (this.paymentMethod = pm),
            error: () => (this.paymentMethodError = true),
          });
        }
      }
      // Load feature sets — available permissions are filtered by org features on backend
      this.featureSetService.loadFeatureSets().subscribe();

      this.featureSetService.loadAvailablePermissions().subscribe((perms) => {
        this.availablePermissions = perms;
        this.showFeatureSets = this.availablePermissions.length > 0;
      });
    });
  }

  /** Flip the tenant-wide requirement, with a confirmation either way.
   *
   *  Both directions change how every colleague signs in tomorrow, so neither
   *  happens on a single click. The dialog spells out the consequence and, for
   *  switching on, that it is reversible — otherwise it reads like a one-way
   *  door and nobody dares touch it.
   */
  async onRequire2faChange(next: boolean): Promise<void> {
    if (this.require2faSaving) {
      return;
    }
    const previous = this.require2fa;

    const confirmed = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: next
            ? 'pages.organisation.security.confirmOn.title'
            : 'pages.organisation.security.confirmOff.title',
          message: next
            ? 'pages.organisation.security.confirmOn.message'
            : 'pages.organisation.security.confirmOff.message',
          details: next
            ? 'pages.organisation.security.confirmOn.details'
            : 'pages.organisation.security.confirmOff.details',
          confirmLabel: next
            ? 'pages.organisation.security.confirmOn.confirm'
            : 'pages.organisation.security.confirmOff.confirm',
          confirmStatus: 'primary',
        },
      }).onClose,
    );

    if (!confirmed) {
      this.restoreRequire2faToggle(previous);
      return;
    }

    this.require2faSaving = true;
    try {
      this.require2fa = await this.twoFactor.setRequirement(next);
      this.toastr.success(
        this.translate.instant(
          this.require2fa
            ? 'pages.organisation.security.enabled'
            : 'pages.organisation.security.disabled',
        ),
        this.translate.instant('pages.organisation.security.title'),
      );
    } catch (e: any) {
      this.restoreRequire2faToggle(previous);
      this.toastr.danger(
        e?.error?.detail || this.translate.instant('pages.twofa.genericError'),
        this.translate.instant('pages.organisation.security.title'),
      );
    } finally {
      this.require2faSaving = false;
    }
  }

  /** Put the switch back where the server has it.
   *
   *  Nebular flips the control's own state on click. Assigning the bound
   *  property the value it already had is a no-op to change detection, so the
   *  switch would keep showing a state the server rejected — which is exactly
   *  what happened with the checkbox this replaced. Writing the opposite and
   *  back forces the control to re-read.
   */
  private restoreRequire2faToggle(value: boolean): void {
    this.require2fa = !value;
    this.cd.detectChanges();
    this.require2fa = value;
    this.cd.detectChanges();
  }


  /** Clear a user's second factor after they lost their device. */
  async onResetTwoFactor(user: User): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: this.translate.instant('pages.organisation.security.resetTitle'),
          message: this.translate.instant('pages.organisation.security.resetMessage', {
            name: user.name || user.email,
          }),
        },
      }).onClose,
    );
    if (!confirmed) {
      return;
    }
    try {
      await this.twoFactor.resetForUser(String(user.id));
      this.toastr.success(
        this.translate.instant('pages.organisation.security.resetDone'),
        this.translate.instant('pages.organisation.security.title'),
      );
    } catch (e: any) {
      this.toastr.danger(
        e?.error?.detail || this.translate.instant('pages.twofa.genericError'),
        this.translate.instant('pages.organisation.security.title'),
      );
    }
  }


  onTabChange(tabId: string): void {
    if (!this.tabIds.includes(tabId as any) || tabId === this.activeTab) {
      return;
    }
    this.activeTab = tabId;
    // replaceUrl: switching a section is not a navigation step someone wants
    // to walk back through with the browser's back button.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }


  protected get organisation() {
    return this.organisationService.organisation;
  }

  // ─── Credits (limited testing: super_admin only) ────────────────────────────

  creditPricing: CreditPricing | null = null;

  private loadCredits() {
    this.creditsLoading = true;
    this.creditService.getBalance().subscribe({
      next: () => (this.creditsLoading = false),
      error: () => (this.creditsLoading = false),
    });
    this.creditService.getSettings().subscribe();
    // Credit-denominated plan allowance for the balance tiles. The tiles used
    // to render raw TOKEN counts; tokens are an internal detail and must never
    // be shown to the customer.
    this.creditService.getPricing().subscribe({
      next: (pricing) => (this.creditPricing = pricing),
      error: () => (this.creditPricing = null),
    });
  }

  /** Plan allowance in credits, or null when the plan grants no credit budget
   *  (rendered as "unlimited"). */
  get planCreditsRemaining(): number | null {
    return this.creditPricing?.plan.remaining_credits ?? null;
  }

  /** Plan credits + purchased credits — what the operator can actually spend. */
  get totalCreditsAvailable(): number | null {
    const plan = this.planCreditsRemaining;
    if (plan === null) return null;
    return plan + (this.creditService.balance$.value?.credits_remaining ?? 0);
  }

  openCreditsInfo() {
    this.dialogService.open(CreditsInfoDialogComponent);
  }

  /** Percent of the combined (plan + credits) allowance already used. Null when
   *  the plan is unlimited — there is nothing to fill a bar with. */
  get creditsUsedPercent(): number | null {
    const balance = this.creditService.balance$.value;
    if (!balance) return null;
    // On the credit basis the bar follows the book that blocks the client.
    if (balance.quota_basis === 'credits') return balance.credit_percent_used ?? null;
    const limit = balance.combined_token_quota_limit;
    if (limit <= 0) return null;
    const used = limit - balance.combined_remaining_tokens;
    return Math.max(0, Math.min(100, Math.round((used / limit) * 100)));
  }

  openBuyCredits() {
    const ctx = this.dialogService.open(BuyCreditsDialogComponent, {
      context: { balance: this.creditService.balance$.value },
    });
    // The dialog navigates away to Stripe on success; on cancel just refresh so
    // a purchase completed in another tab shows up.
    ctx.onClose.subscribe(() => this.loadCredits());
  }

  private saveCreditSettings(payload: Partial<Record<keyof CreditSettings, unknown>>) {
    this.creditService.updateSettings(payload).subscribe({
      next: () =>
        this.toastr.success(
          this.translate.instant('pages.organisation.credits.saved'),
          this.translate.instant('pages.organisation.credits.toastSavedTitle')
        ),
      error: (err) => {
        // Re-read so the UI never shows a value the backend rejected.
        this.creditService.getSettings().subscribe();
        const detail = err?.error?.detail;
        this.toastr.danger(
          detail || this.translate.instant('pages.organisation.credits.errors.generic'),
          this.translate.instant('pages.organisation.credits.toastErrorTitle')
        );
      },
    });
  }

  /** Toggling auto-recharge. Turning OFF just saves. Turning ON first shows a
   *  confirmation dialog explaining what it does; on confirm we enable it —
   *  and if the backend reports no authorised card yet (SCA gate), we send the
   *  client to Stripe via the buy flow to authorise their card, after which
   *  they can enable it. */
  /** Toggling the auto-recharge switch. ON → the settings dialog (enter the two
   *  values + cap) then confirm → save + enable → Stripe if a card is still
   *  needed. OFF → a confirmation dialog first (so it isn't disabled by
   *  accident). The toggle itself is never the source of truth; we re-read. */
  onAutoRechargeToggle(enabled: boolean, settings: CreditSettings) {
    if (enabled) {
      this.openAutoRechargeDialog(settings, true);
    } else {
      this.confirmDisableAutoRecharge();
    }
  }

  /** Opens the edit dialog for an already-active auto-recharge. */
  editAutoRecharge(settings: CreditSettings) {
    this.openAutoRechargeDialog(settings, false);
  }

  private openAutoRechargeDialog(settings: CreditSettings, enabling: boolean) {
    const ctx = this.dialogService.open(AutoRechargeDialogComponent, {
      context: { settings, enabling },
    });
    ctx.onClose.subscribe((result?: AutoRechargeDialogResult) => {
      if (!result) {
        // Cancelled — snap the toggle back to its stored state.
        this.creditService.getSettings().subscribe();
        return;
      }
      if (result.action === 'disable') {
        this.confirmDisableAutoRecharge();
        return;
      }
      this.saveAndEnableAutoRecharge(result);
    });
  }

  private saveAndEnableAutoRecharge(result: AutoRechargeDialogResult) {
    const balance = this.creditService.balance$.value;
    const threshold = result.threshold_eur!;
    const amount = result.amount_eur!;

    // No card on file yet → run a Stripe checkout for the first top-up (amount).
    // It saves the card (setup_future_usage) and the webhook enables
    // auto-recharge with these values. No separate "buy first" detour.
    if (!balance?.has_saved_card) {
      if (!balance?.can_buy) {
        this.creditService.getSettings().subscribe();
        this.toastr.danger(
          this.translate.instant('pages.organisation.credits.cannotBuy.' + balance?.cannot_buy_reason, {
            default: this.translate.instant('pages.organisation.credits.errors.generic'),
          }),
          this.translate.instant('pages.organisation.credits.toastErrorTitle')
        );
        return;
      }
      this.creditService
        .createCheckout(amount, {
          enable_auto_recharge: true,
          auto_recharge_threshold_eur: threshold,
          auto_recharge_amount_eur: amount,
        })
        .subscribe({
          next: (data) => window.open(data.payment_link, '_self'),
          error: () => {
            this.creditService.getSettings().subscribe();
            this.toastr.danger(
              this.translate.instant('pages.organisation.credits.autoRecharge.dialog.title'),
              this.translate.instant('pages.organisation.credits.toastErrorTitle')
            );
          },
        });
      return;
    }

    // Card already on file → just enable directly.
    this.creditService
      .updateSettings({
        auto_recharge_threshold_eur: threshold,
        auto_recharge_amount_eur: amount,
        auto_recharge_enabled: true,
      })
      .subscribe({
        next: () =>
          this.toastr.success(
            this.translate.instant('pages.organisation.credits.saved'),
            this.translate.instant('pages.organisation.credits.toastSavedTitle')
          ),
        error: (err) => {
          this.creditService.getSettings().subscribe();
          const detail = err?.error?.detail;
          this.toastr.danger(
            detail
              ? this.translate.instant('pages.organisation.credits.errors.' + detail, {
                  default: detail,
                })
              : this.translate.instant('pages.organisation.credits.errors.generic'),
            this.translate.instant('pages.organisation.credits.toastErrorTitle')
          );
        },
      });
  }

  private confirmDisableAutoRecharge() {
    const ctx = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.organisation.credits.autoRecharge.disableConfirm.title',
        message: 'pages.organisation.credits.autoRecharge.disableConfirm.message',
        confirmLabel: 'pages.organisation.credits.autoRecharge.disableConfirm.confirm',
        cancelLabel: 'buttons.cancel',
        confirmStatus: 'primary',
        cancelStatus: 'basic',
      },
    });
    ctx.onClose.subscribe((confirmed: boolean) => {
      if (!confirmed) {
        this.creditService.getSettings().subscribe();
        return;
      }
      this.saveCreditSettings({ auto_recharge_enabled: false });
    });
  }

  toggleAlertThreshold(settings: CreditSettings, threshold: number) {
    const next = settings.alert_thresholds.includes(threshold)
      ? settings.alert_thresholds.filter((t) => t !== threshold)
      : [...settings.alert_thresholds, threshold].sort((a, b) => a - b);
    if (next.length === 0) {
      this.toastr.warning(
        this.translate.instant('pages.organisation.credits.alerts.atLeastOne'),
        this.translate.instant('pages.organisation.credits.toastErrorTitle')
      );
      return;
    }
    this.saveCreditSettings({ alert_thresholds: next });
  }

  /** Toggling "use default" on sends null, which makes the backend fall back to
   *  the global admin default. */
  setAlertsDefault(useDefault: boolean) {
    if (!useDefault) return;
    this.saveCreditSettings({ alert_thresholds: null });
  }

  /** Optional monthly spending limit. ON seeds a starting value; OFF clears it
   *  (null = no limit). Applies to auto-recharge only. */
  onLimitToggle(enabled: boolean, settings: CreditSettings) {
    if (enabled) {
      const value = settings.auto_recharge_monthly_cap_eur ?? 100;
      this.saveCreditSettings({ auto_recharge_monthly_cap_eur: value });
    } else {
      this.saveCreditSettings({ auto_recharge_monthly_cap_eur: null });
    }
  }

  /** Committing a value sets the monthly limit AND turns it on. Entering a
   *  value is enough — no need to flip the toggle first. */
  onLimitAmountChange(value: string | number) {
    const parsed = Math.floor(Number(value));
    if (!Number.isFinite(parsed) || parsed < 10) return;
    if (parsed === this.creditService.settings$.value?.auto_recharge_monthly_cap_eur) return;
    this.saveCreditSettings({ auto_recharge_monthly_cap_eur: parsed });
  }

  acknowledgeAutoRecharge() {
    this.creditService.acknowledgeAutoRechargeFailure().subscribe({
      next: () =>
        this.toastr.success(
          this.translate.instant('pages.organisation.credits.autoRecharge.reactivated'),
          this.translate.instant('pages.organisation.credits.toastSavedTitle')
        ),
      error: () =>
        this.toastr.danger(
          this.translate.instant('pages.organisation.credits.errors.generic'),
          this.translate.instant('pages.organisation.credits.toastErrorTitle')
        ),
    });
  }

  onEditUser(user: User) {
    const ctx = this.dialogService.open(UserDialogComponent, {
      context: {
        data: user,
      },
    });

    ctx.onClose.subscribe(() => {
      this.userService.listUsers().subscribe();
    });
  }

  onAddUser() {
    this.dialogService.open(InviteUserDialogComponent);
  }

  /** Invitation mails get lost, and a temporary password mistyped once looks
   *  exactly like a wrong one. This sends a new password and a new mail —
   *  confirmed first, because the old password stops working. */
  async onResendInvitation(user: User): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: this.translate.instant('pages.organisation.invitations.resendTitle'),
          message: this.translate.instant('pages.organisation.invitations.resendMessage', {
            name: user.name || user.email,
          }),
        },
      }).onClose,
    );
    if (!confirmed) {
      return;
    }
    this.resendingInvitationFor = String(user.id);
    this.userService.resendInvitation(String(user.id)).subscribe({
      next: () => {
        this.resendingInvitationFor = null;
        this.toastr.success(
          this.translate.instant('pages.organisation.invitations.resendDone', {
            email: user.email,
          }),
          this.translate.instant('pages.organisation.titleUsers'),
        );
        this.userService.listUsers().subscribe();
      },
      error: (e: any) => {
        this.resendingInvitationFor = null;
        this.toastr.danger(
          e?.error?.detail ||
            this.translate.instant('pages.organisation.invitations.resendError'),
          this.translate.instant('pages.organisation.titleUsers'),
        );
      },
    });
  }

  get invoices() {
    return this.organisationService.invoices$.value;
  }

  /** No default payment method although the plan renews automatically —
   *  the next renewal fails with `requires_payment_method`. */
  get paymentMethodMissing(): boolean {
    const pm = this.paymentMethod;
    return !!pm?.has_customer && !pm.payment_method && pm.source !== 'legacy_source'
      && !!pm.subscription_status && pm.subscription_status !== 'canceled'
      && pm.collection_method !== 'send_invoice';
  }

  onOpenBillingPortal() {
    if (this.billingPortalOpening) {
      return;
    }
    this.billingPortalOpening = true;
    this.organisationService.openBillingPortal().subscribe({
      next: (url) => (window.location.href = url),
      error: () => {
        this.billingPortalOpening = false;
        this.toastr.danger(
          this.translate.instant('pages.organisation.paymentMethod.portalError'),
          ''
        );
      },
    });
  }

  get activeProduct() {
    return this.productService.activeProduct$.value;
  }

  get activeProdcutName() {
    const plan = this.productService.currentPlan$.value?.current_plan;
    if (plan?.display_name) {
      return plan.display_name;
    }
    return this.activeProduct?.name || 'custom';
  }

  /** Individueller Vertrag: statt Stripe-Zahlung und -Rechnungen die
   *  Vertragskarte und ein Hinweis auf Rechnungen per E-Mail. */
  get isContract(): boolean {
    return this.productService.isContract;
  }

  private loadContract() {
    this.contractInvoicesLoading = true;
    this.contractService
      .getOwnInvoices()
      .pipe(finalize(() => (this.contractInvoicesLoading = false)))
      .subscribe({
        next: (invoices) =>
          (this.contractInvoices = invoices.map((invoice) => ({
            ...invoice,
            periodTo: lastDayOf(invoice.service_end || invoice.usage_end),
          }))),
        error: () => (this.contractInvoices = []),
      });
    this.contractLoading = true;
    this.contractService
      .getOwnContract()
      .pipe(finalize(() => (this.contractLoading = false)))
      .subscribe({
        next: (contract) => {
          this.contract = contract;
          this.contractLimitRows = CONTRACT_LIMIT_KEYS.filter(
            (key) => contract?.limits?.[key] != null
          ).map((key) => ({
            key,
            value: this.formatContractLimit(key, contract!.limits![key] as number),
          }));
        },
        error: () => (this.contract = null),
      });
  }

  private formatContractLimit(key: string, value: number): string {
    if (value === -1) {
      return this.translate.instant('pages.organisation.contract.unlimited');
    }
    const locale = this.translate.currentLang === 'en' ? 'en-GB' : 'de-DE';
    if (key === 'storage_bytes') {
      return `${Math.round(value / BYTES_PER_MB).toLocaleString(locale)} MB`;
    }
    return value.toLocaleString(locale);
  }

  get productPrice() {
    return this.activeProduct?.price ? this.activeProduct.price / 100 : 0;
  }

  onDownloadContractInvoice(invoice: ContractInvoice) {
    if (this.downloadingInvoiceId) return;
    this.downloadingInvoiceId = invoice.id;
    this.contractService
      .getOwnInvoicePdf(invoice.id)
      .pipe(finalize(() => (this.downloadingInvoiceId = undefined)))
      .subscribe({
        next: (blob) => openPdfBlob(blob, `${invoice.number || invoice.id}.pdf`),
        error: () => undefined,
      });
  }

  onDownloadInvoice(event: any, invoice: Invoice) {
    event.stopPropagation();
    open(invoice.invoice_pdf);
  }

  openStripe(invoice: Invoice) {
    open(invoice.hosted_invoice_url);
  }

  openAvvDialog() {
    this.organisationService.getAvvStatus().subscribe((status) => {
      const ref = this.dialogService.open(AvvDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: false,
        context: {
          data: status,
        },
      });
      ref.onClose.subscribe(() => {
        this.organisationService.getOrganisation().subscribe();
      });
    });
  }

  onDeleteUser(user: User) {
    this.userService.deleteUser(user.id).subscribe(() => {
      this.userService.listUsers().subscribe();
    });
  }

  onDeleteClient() {
    if (this.deletingOrganisation) return;
    const ctx = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'dialogs.confirmDialog.deleteOrganisation.title',
        message: 'dialogs.confirmDialog.deleteOrganisation.message',
      },
    });
    ctx.onClose.subscribe(async (isConfirmed: boolean) => {
      if (isConfirmed) {
        if (this.deletingOrganisation) return;
        this.deletingOrganisation = true;
        // On success the service logs out; the flag only comes back on error.
        this.organisationService
          .deleteMyOrganisation()
          .pipe(finalize(() => (this.deletingOrganisation = false)))
          .subscribe();
      }
    });
  }

  // ─── Feature Set Management ──────────────────────────────────

  get featureSets() {
    return this.featureSetService.featureSets$.value;
  }

  // Map any granular permission to its high-level org feature category.
  // Only these categories are shown as chips in the list (with i18n).
  displayPermissions(perms: string[]): string[] {
    const out = new Set<string>();
    for (const p of perms || []) {
      if (p === 'DOZENT' || p.startsWith('Dozent')) out.add('DOZENT');
      else if (p === 'ANALYTICS' || p.startsWith('Analytics'))
        out.add('ANALYTICS');
      else if (p === 'CONTENT_GENERATOR' || p === 'Creator')
        out.add('CONTENT_GENERATOR');
    }
    return Array.from(out);
  }

  getFeatureSetDisplayName(featureSet: FeatureSet) {
    return this.featureSetService.getDisplayName(featureSet);
  }

  openFeatureSetDialog(fs: FeatureSet | null = null) {
    this.dialogService.open(FeatureSetDialogComponent, {
      context: {
        featureSet: fs,
        availablePermissions: this.availablePermissions,
      },
    });
  }

  deleteFeatureSet(fs: FeatureSet) {
    if (fs.is_default) return;
    const ctx = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.organisation.featureSets.confirmDelete',
        message: 'pages.organisation.featureSets.confirmDeleteMessage',
      },
    });
    ctx.onClose.subscribe((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.featureSetService.deleteFeatureSet(fs.id).subscribe(() => {
          this.toastr.success('Feature Set gelöscht', 'Gelöscht');
        });
      }
    });
  }
}
